/**
 * SkillRadarChart
 *
 * Radar/spider chart showing golfer skill profile.
 * Handicap displayed in centre, skill shape shown as polygon.
 */

import React, { useMemo } from 'react';
import { SKILL_LABELS } from '../utility/golferProfiles';

/**
 * Convert skill value (-0.8 to +0.8) to radius percentage (0.2 to 1.0)
 * Neutral (0) = 0.6, which is 60% of max radius
 */
const skillToRadius = (value) => {
    // Map -0.8 to +0.8 → 0.2 to 1.0
    // 0 maps to 0.6 (neutral point)
    return 0.6 + (value * 0.5);
};

/**
 * Convert consistency value (0.7 to 1.4) to radius percentage (0.2 to 1.0)
 * Lower consistency value = more consistent = larger radius (better)
 * 1.0 (average) maps to 0.6 (neutral point)
 */
const consistencyToRadius = (value) => {
    // Invert: 0.7 → 1.0, 1.0 → 0.6, 1.4 → 0.2
    // Formula: 1.0 - (value - 0.7) * (0.8 / 0.7) = 1.0 - (value - 0.7) * 1.14
    const inverted = 1.0 - (value - 0.7) * 1.14;
    return Math.max(0.2, Math.min(1.0, inverted));
};

/**
 * Convert consistency value to equivalent skill value for colour coding
 */
const consistencyToSkillEquivalent = (value) => {
    // 0.7 → +0.8 (excellent), 1.0 → 0 (average), 1.4 → -0.8 (poor)
    return (1.0 - value) * (0.8 / 0.3);
};

/**
 * Generate points for a regular polygon
 */
const generatePolygonPoints = (sides, radius, centerX, centerY, startAngle = -Math.PI / 2) => {
    const points = [];
    for (let i = 0; i < sides; i++) {
        const angle = startAngle + (2 * Math.PI * i) / sides;
        points.push({
            x: centerX + radius * Math.cos(angle),
            y: centerY + radius * Math.sin(angle)
        });
    }
    return points;
};

/**
 * Generate skill polygon points based on skill values (5 dimensions including consistency)
 */
const generateSkillPolygon = (skills, consistency, maxRadius, centerX, centerY) => {
    const dimensions = [
        { key: 'offTheTee', value: skills.offTheTee, isConsistency: false },
        { key: 'approach', value: skills.approach, isConsistency: false },
        { key: 'aroundGreen', value: skills.aroundGreen, isConsistency: false },
        { key: 'putting', value: skills.putting, isConsistency: false },
        { key: 'consistency', value: consistency, isConsistency: true }
    ];
    const startAngle = -Math.PI / 2; // Start from top

    return dimensions.map((dim, i) => {
        const angle = startAngle + (2 * Math.PI * i) / dimensions.length;
        const radius = dim.isConsistency
            ? consistencyToRadius(dim.value) * maxRadius
            : skillToRadius(dim.value) * maxRadius;
        const displayValue = dim.isConsistency
            ? consistencyToSkillEquivalent(dim.value)
            : dim.value;
        return {
            x: centerX + radius * Math.cos(angle),
            y: centerY + radius * Math.sin(angle),
            skill: dim.key,
            value: dim.value,
            displayValue // For colour coding
        };
    });
};

/**
 * Points to SVG path string
 */
const pointsToPath = (points) => {
    if (points.length === 0) return '';
    const start = `M ${points[0].x} ${points[0].y}`;
    const lines = points.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ');
    return `${start} ${lines} Z`;
};

/**
 * Get colour based on skill value
 */
const getSkillColour = (value) => {
    if (value >= 0.4) return '#22C55E';  // Green - excellent
    if (value >= 0.15) return '#84CC16'; // Lime - good
    if (value >= -0.15) return '#6B7280'; // Grey - average
    if (value >= -0.4) return '#F59E0B'; // Amber - below average
    return '#EF4444';                     // Red - weak
};

/**
 * Labels for all 5 dimensions
 */
const DIMENSION_LABELS = {
    offTheTee: 'Off the Tee',
    approach: 'Approach',
    aroundGreen: 'Around Green',
    putting: 'Putting',
    consistency: 'Consistency'
};

const DIMENSION_SHORT_LABELS = {
    offTheTee: 'Drive',
    approach: 'App',
    aroundGreen: 'Short',
    putting: 'Putt',
    consistency: 'Cons'
};

/**
 * Main radar chart component
 */
export const SkillRadarChart = ({
    skills,
    handicap,
    consistency = 1.0,
    size = 180,
    showLabels = true,
    showValues = false,
    className = ''
}) => {
    // Leave more padding for labels
    const padding = showLabels ? 32 : 8;
    const center = size / 2;
    const maxRadius = (size / 2) - padding;

    // 5 dimensions for pentagon
    const numDimensions = 5;

    // Generate grid rings (pentagon shape)
    const rings = useMemo(() => [0.2, 0.4, 0.6, 0.8, 1.0].map(scale => ({
        radius: maxRadius * scale,
        isNeutral: scale === 0.6
    })), [maxRadius]);

    // Generate axis lines for 5 dimensions
    const axes = useMemo(() => {
        const dimensionKeys = ['offTheTee', 'approach', 'aroundGreen', 'putting', 'consistency'];
        return dimensionKeys.map((key, i) => {
            const angle = -Math.PI / 2 + (2 * Math.PI * i) / numDimensions;

            // Position labels just outside the chart, with adjustments to stay in bounds
            const labelDistance = maxRadius + 12;
            let labelX = center + labelDistance * Math.cos(angle);
            let labelY = center + labelDistance * Math.sin(angle);

            return {
                key,
                label: DIMENSION_LABELS[key],
                shortLabel: DIMENSION_SHORT_LABELS[key],
                x1: center,
                y1: center,
                x2: center + maxRadius * Math.cos(angle),
                y2: center + maxRadius * Math.sin(angle),
                labelX,
                labelY,
                angle
            };
        });
    }, [center, maxRadius, numDimensions]);

    // Generate skill polygon (5 points)
    const skillPoints = useMemo(() =>
        generateSkillPolygon(skills, consistency, maxRadius, center, center),
        [skills, consistency, maxRadius, center]
    );

    // Calculate overall skill quality for polygon fill (include consistency)
    const consistencyAsSkill = consistencyToSkillEquivalent(consistency);
    const avgSkill = (skills.offTheTee + skills.approach + skills.aroundGreen + skills.putting + consistencyAsSkill) / 5;
    const polygonColour = getSkillColour(avgSkill);

    return (
        <div className={`relative ${className}`}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                {/* Background rings (pentagon) */}
                {rings.map((ring, i) => (
                    <polygon
                        key={i}
                        points={generatePolygonPoints(numDimensions, ring.radius, center, center)
                            .map(p => `${p.x},${p.y}`).join(' ')}
                        fill="none"
                        stroke={ring.isNeutral ? '#9CA3AF' : '#E5E7EB'}
                        strokeWidth={ring.isNeutral ? 1.5 : 1}
                        strokeDasharray={ring.isNeutral ? '4,2' : 'none'}
                    />
                ))}

                {/* Axis lines */}
                {axes.map(axis => (
                    <line
                        key={axis.key}
                        x1={axis.x1}
                        y1={axis.y1}
                        x2={axis.x2}
                        y2={axis.y2}
                        stroke="#E5E7EB"
                        strokeWidth={1}
                    />
                ))}

                {/* Skill polygon - filled area */}
                <path
                    d={pointsToPath(skillPoints)}
                    fill={polygonColour}
                    fillOpacity={0.3}
                    stroke={polygonColour}
                    strokeWidth={2}
                />

                {/* Skill points */}
                {skillPoints.map((point, i) => (
                    <circle
                        key={i}
                        cx={point.x}
                        cy={point.y}
                        r={4}
                        fill={getSkillColour(point.displayValue)}
                        stroke="white"
                        strokeWidth={1.5}
                    />
                ))}

                {/* Axis labels */}
                {showLabels && axes.map((axis, i) => {
                    // Better positioning based on angle
                    // Pentagon angles: top, upper-right, lower-right, lower-left, upper-left
                    let textAnchor = 'middle';
                    let dx = 0;
                    let dy = 0;

                    // Top (index 0): center, above
                    if (i === 0) {
                        dy = -2;
                    }
                    // Upper-right (index 1): start anchor, slight down
                    else if (i === 1) {
                        textAnchor = 'start';
                        dx = 2;
                        dy = 4;
                    }
                    // Lower-right (index 2): start anchor, slight up
                    else if (i === 2) {
                        textAnchor = 'start';
                        dx = 2;
                        dy = 4;
                    }
                    // Lower-left (index 3): end anchor
                    else if (i === 3) {
                        textAnchor = 'end';
                        dx = -2;
                        dy = 4;
                    }
                    // Upper-left (index 4): end anchor
                    else if (i === 4) {
                        textAnchor = 'end';
                        dx = -2;
                        dy = 4;
                    }

                    return (
                        <text
                            key={axis.key}
                            x={axis.labelX + dx}
                            y={axis.labelY + dy}
                            textAnchor={textAnchor}
                            fontSize="9"
                            fill="#6B7280"
                            fontWeight="500"
                        >
                            {axis.shortLabel}
                        </text>
                    );
                })}

                {/* Centre circle with handicap */}
                {(() => {
                    // Scale centre circle and text based on chart size
                    const circleRadius = Math.max(14, size * 0.1);
                    const mainFontSize = Math.max(10, size * 0.075);
                    const labelFontSize = Math.max(5, size * 0.035);
                    const labelOffset = circleRadius * 0.6;

                    return (
                        <>
                            <circle
                                cx={center}
                                cy={center}
                                r={circleRadius}
                                fill="white"
                                stroke="#E5E7EB"
                                strokeWidth={1}
                            />
                            <text
                                x={center}
                                y={center}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontSize={mainFontSize}
                                fontWeight="bold"
                                fill="#1F2937"
                            >
                                {handicap}
                            </text>
                            <text
                                x={center}
                                y={center + labelOffset}
                                textAnchor="middle"
                                fontSize={labelFontSize}
                                fill="#9CA3AF"
                            >
                                hcp
                            </text>
                        </>
                    );
                })()}
            </svg>

            {/* Value tooltips on hover - could add later */}
            {showValues && (
                <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-2 text-xs text-gray-500">
                    {Object.entries(skills).map(([key, value]) => (
                        <span key={key} style={{ color: getSkillColour(value) }}>
                            {value >= 0 ? '+' : ''}{value.toFixed(1)}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Compact version for lists/cards
 */
export const SkillRadarChartCompact = ({ skills, handicap, consistency = 1.0, size = 80 }) => {
    return (
        <SkillRadarChart
            skills={skills}
            handicap={handicap}
            consistency={consistency}
            size={size}
            showLabels={false}
            showValues={false}
        />
    );
};

/**
 * Legend component explaining the chart
 */
export const SkillRadarLegend = () => (
    <div className="text-xs text-gray-500 space-y-1">
        <p className="font-medium mb-2">5 Dimensions:</p>
        <p>Drive, Approach, Short Game, Putting, Consistency</p>
        <p className="mt-2"><span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1"></span> Strong</p>
        <p><span className="inline-block w-3 h-3 rounded-full bg-gray-400 mr-1"></span> Average</p>
        <p><span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-1"></span> Weak</p>
        <p className="mt-2 italic">Dashed ring = neutral baseline</p>
    </div>
);

export default SkillRadarChart;
